import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../components/Icon";
import { useAuth } from "../auth";
import { useI18n } from "../i18n";
import { apiGet } from "../api";
import useTodayChecks, { fmtCheckTime } from "../useTodayChecks";
import { C, R, S } from "../theme";

export default function Attractions() {
  const { isManager } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const { checks } = useTodayChecks();

  const load = async () => {
    try {
      const [a, s] = await Promise.all([apiGet("/attractions"), apiGet("/subscription").catch(() => null)]);
      setItems(a); setSub(s);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const lockedIds = sub?.locked_attraction_ids || [];
  const canAdd = sub ? sub.can_add : true;
  const showUnlock = !!sub && (lockedIds.length > 0 || !canAdd);
  const onAdd = () => { if (sub && !sub.can_add) nav("/paywall"); else nav("/attraction/new"); };

  const unlockMsg = lockedIds.length > 0
    ? `${lockedIds.length} attraction${lockedIds.length === 1 ? "" : "s"} ${lockedIds.length === 1 ? "is" : "are"} locked — they're over your ${sub?.tier_label} plan limit. Your data is safe. Subscribe to unlock editing & inspections.`
    : `You've reached your ${sub?.tier_label} plan limit (${sub?.attraction_limit}). Subscribe to add more attractions.`;

  return (
    <div className="fade-up" style={{ minHeight: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: S.lg, paddingBottom: S.sm }}>
        <h1 className="display" style={{ color: C.text, fontSize: 28, fontWeight: 900, margin: 0 }}>{t("attractions.title")}</h1>
        {!!sub && <div style={{ color: C.amber, fontWeight: 800 }}>{sub.attraction_count}/{sub.is_exempt_from_billing ? "∞" : sub.attraction_limit} {t("attractions.used")}</div>}
      </div>
      <div style={{ padding: `0 ${S.lg}px ${S.xxl}px` }}>
        {showUnlock && (
          <div data-testid="unlock-banner" style={{ background: C.card, borderRadius: R.md, padding: S.lg, marginBottom: S.md, border: `1px solid ${C.amber}` }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: S.md }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(245,158,11,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon name="lock-closed" size={20} color={C.amber} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ color: C.text, fontSize: 15, fontWeight: 800 }}>{t("attractions.subscribeUnlock")}</div>
                <div style={{ color: C.muted, fontSize: 13, marginTop: 3, lineHeight: 1.4 }}>{unlockMsg}</div>
              </div>
            </div>
            <button data-testid="unlock-banner-btn" className="pressable" onClick={() => nav("/paywall")} style={{ marginTop: S.md, width: "100%", background: C.amber, borderRadius: R.pill, minHeight: 46, display: "flex", alignItems: "center", justifyContent: "center", gap: S.sm, color: C.black, fontWeight: 900, fontSize: 14, border: "none" }}>
              <Icon name="rocket" size={16} color={C.black} /> {lockedIds.length > 0 ? t("attractions.subscribeToUnlock") : t("attractions.choosePlan")}
            </button>
          </div>
        )}

        {isManager && (
          <div data-testid="share-pack-btn" className="pressable" onClick={() => nav("/share-pack")} style={{ display: "flex", alignItems: "center", gap: S.md, background: C.amber, borderRadius: R.md, padding: S.lg, marginBottom: S.md, cursor: "pointer" }}>
            <div style={{ width: 46, height: 46, borderRadius: 12, background: "rgba(0,0,0,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon name="paper-plane" size={26} color={C.black} />
            </div>
            <div style={{ flex: 1 }}>
              <div className="display" style={{ color: C.black, fontSize: 18, fontWeight: 900 }}>{t("attractions.sendPaperworkFast")}</div>
              <div style={{ color: C.black, fontSize: 13, fontWeight: 600, opacity: 0.8, marginTop: 2 }}>{t("attractions.sendPaperworkSub")}</div>
            </div>
            <Icon name="chevron-forward" size={24} color={C.black} />
          </div>
        )}

        {loading ? (
          <div style={{ color: C.amber, textAlign: "center", marginTop: S.xl }}>{t("actions.loading")}</div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: "center", padding: `${S.xxl}px 0`, color: C.muted }}>
            <Icon name="cube-outline" size={48} color={C.muted} />
            <div style={{ color: C.text, fontSize: 17, fontWeight: 700, marginTop: S.md }}>{t("attractions.noAttractions")}</div>
          </div>
        ) : (
          <div data-testid="attractions-list">
            {items.map((a) => {
              const inflatable = a.category === "inflatable";
              const maint = a.status === "out_of_service";
              const locked = lockedIds.includes(a.id);
              const typeLabel = a.category === "catering" ? t("attractions.typeCatering") : inflatable ? t("attractions.typeInflatable") : t("attractions.typeRide");
              return (
                <div key={a.id} data-testid={`attraction-${a.id}`} style={{ background: C.card, borderRadius: R.md, border: `1px solid ${locked ? C.amber : C.border}`, marginBottom: S.md, overflow: "hidden", opacity: locked ? 0.72 : 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: S.md, padding: S.md }}>
                    <div className="pressable" onClick={() => nav(`/attraction/${a.id}`)} style={{ display: "flex", alignItems: "center", gap: S.md, flex: 1, minWidth: 0, cursor: "pointer" }}>
                      <div style={{ width: 56, height: 56, borderRadius: 12, aspectRatio: "1 / 1", background: a.category === "catering" ? "#f97316" : inflatable ? C.blue : C.amber, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
                        {a.photo ? <img src={a.photo} alt={a.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} /> : <Icon name={a.category === "catering" ? "restaurant" : inflatable ? "balloon" : "cog"} size={28} color={C.black} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: C.text, fontSize: 17, fontWeight: 800, lineHeight: 1.2 }}>{a.name}</div>
                        {locked ? (
                          <div data-testid={`locked-${a.id}`} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: C.amber, padding: "2px 8px", borderRadius: R.sm, marginTop: 4 }}>
                            <Icon name="lock-closed" size={11} color={C.black} /><span style={{ color: C.black, fontWeight: 900, fontSize: 10, letterSpacing: 0.5 }}>{t("status.locked")}</span>
                          </div>
                        ) : maint ? (
                          <div data-testid={`oos-${a.id}`} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: C.red, padding: "2px 8px", borderRadius: R.sm, marginTop: 4 }}>
                            <Icon name="ban" size={11} color={C.text} /><span style={{ color: C.text, fontWeight: 900, fontSize: 10, letterSpacing: 0.5 }}>{t("status.outOfService")}</span>
                          </div>
                        ) : (
                          <div style={{ color: C.muted, fontSize: 13, marginTop: 2 }}>{typeLabel}{a.category === "catering" ? ` · ${t("attractions.sfbb")}` : ` · ${a.checklist.length} ${t("attractions.items")}`}</div>
                        )}
                        {!locked && !maint && a.category !== "catering" && (checks[a.id] ? (
                          <div data-testid={`daily-status-done-${a.id}`} style={{ color: C.green, fontSize: 12, fontWeight: 700, marginTop: 4 }}>{t("status.checkedToday")} · {checks[a.id].by || "—"}{checks[a.id].at ? ` · ${fmtCheckTime(checks[a.id].at)}` : ""}</div>
                        ) : (
                          <div data-testid={`daily-status-due-${a.id}`} style={{ color: C.amber, fontSize: 12, fontWeight: 700, marginTop: 4 }}>{t("status.dailyCheckDue")}</div>
                        ))}
                      </div>
                    </div>
                    {isManager && !locked ? (
                      <button data-testid={`edit-attraction-${a.id}`} className="pressable" onClick={(e) => { e.stopPropagation(); nav(`/attraction/${a.id}/edit`); }} style={{ display: "flex", alignItems: "center", gap: 6, background: C.amber, color: C.black, fontWeight: 900, fontSize: 13, border: "none", borderRadius: R.pill, padding: "9px 15px", minHeight: 42, flexShrink: 0, cursor: "pointer" }}>
                        <Icon name="create-outline" size={16} color={C.black} /> {t("actions.edit")}
                      </button>
                    ) : (
                      <Icon name={locked ? "lock-closed" : "chevron-forward"} size={22} color={locked ? C.amber : C.muted} />
                    )}
                  </div>
                  {inflatable && a.delivery_handovers_enabled && !locked && !maint && (
                    <button data-testid={`delivery-handover-${a.id}`} className="pressable" onClick={(e) => { e.stopPropagation(); nav(`/attraction/${a.id}/delivery`); }} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: S.sm, width: "100%", background: C.card2, borderTop: `1px solid ${C.border}`, minHeight: 46, color: C.blue, fontWeight: 800, fontSize: 14, border: "none", cursor: "pointer" }}>
                      <Icon name="clipboard-outline" size={18} color={C.blue} /> {t("attractions.deliveryHandover")}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {isManager && (
          <button data-testid="add-attraction-btn" className="pressable" onClick={onAdd} style={{ marginTop: S.lg, width: "100%", background: canAdd ? C.amber : C.card2, borderRadius: R.md, minHeight: 56, display: "flex", alignItems: "center", justifyContent: "center", gap: S.sm, color: canAdd ? C.black : C.amber, fontWeight: 900, fontSize: 17, border: canAdd ? "none" : `1px solid ${C.amber}` }}>
            <Icon name={canAdd ? "add" : "lock-closed"} size={canAdd ? 26 : 20} color={canAdd ? C.black : C.amber} /> {canAdd ? t("attractions.addAttraction") : t("attractions.subscribeToAddMore")}
          </button>
        )}
      </div>
    </div>
  );
}
